import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * ArrowExpand component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconArrowExpand extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M22.5697 1.45145C22.4697 1.35145 22.3597 1.29145 22.2397 1.26145C22.1897 1.25145 22.1297 1.24145 22.0797 1.23145C22.0697 1.23145 22.0597 1.23145 22.0397 1.23145H15.7997C15.3897 1.23145 15.0497 1.57145 15.0497 1.98145C15.0497 2.39145 15.3897 2.73145 15.7997 2.73145H20.2297L13.7097 9.25145C13.4197 9.54145 13.4197 10.0214 13.7097 10.3114C13.9997 10.6014 14.4797 10.6014 14.7697 10.3114L21.2897 3.79145V8.22145C21.2897 8.63145 21.6297 8.97145 22.0397 8.97145C22.4497 8.97145 22.7897 8.63145 22.7897 8.22145V2.00145C22.7897 1.80145 22.7197 1.60145 22.5697 1.45145V1.45145Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.02 13.9414L2.5 20.4614V16.0314C2.5 15.6214 2.16 15.2814 1.75 15.2814C1.34 15.2814 1 15.6114 1 16.0314V22.2514C1 22.4514 1.07 22.6514 1.22 22.8014C1.32 22.9014 1.43 22.9614 1.55 22.9914C1.6 23.0014 1.66 23.0114 1.71 23.0214C1.72 23.0214 1.73 23.0214 1.75 23.0214H7.99C8.4 23.0214 8.74 22.6814 8.74 22.2714C8.74 21.8614 8.4 21.5214 7.99 21.5214H3.56L10.08 15.0014C10.37 14.7114 10.37 14.2314 10.08 13.9414C9.79 13.6514 9.31 13.6514 9.02 13.9414V13.9414Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-arrow-expand') === undefined) {
  customElements.define('cat-icon-arrow-expand', BlocksIconArrowExpand);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-arrow-expand': BlocksIconArrowExpand;
  }
}
